import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import TrackerTable from "@/components/TrackerTable";
import RepoBulkBar from "@/components/RepoBulkBar";
import {
  FolderTree, Folder, FolderOpen, ChevronRight, ChevronDown,
  Plus, Edit2, Archive, List, LayoutGrid, RefreshCw, Building2, X,
  Search as SearchIcon, CheckCircle2, Clock, Pin, PinOff, History,
} from "lucide-react";

const MANAGE_ROLES = new Set(["it_admin", "legal_manager", "general_counsel", "contract_manager"]);
const BULK_ROLES   = new Set(["it_admin", "legal_manager", "general_counsel"]);

// Non-executed stages surface every open contract in flight.
const NON_EXEC_STAGES = new Set([
  "unassigned","draft","submitted","routing","in_review","approved",
  "analyst_working","attorney_review","gc_review","vendor_review","legal_approved",
  "expired","terminated",
]);

const ARCHIVE_YEARS = 7;

function isArchive(c) {
  const exp = c.expiration_date || c.end_date;
  if (!exp) return false;
  try {
    const d = new Date(exp);
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - ARCHIVE_YEARS);
    return d < cutoff;
  } catch { return false; }
}

const NON_EXEC_STAGE_OPTIONS = [
  { value: "all",             label: "All non-executed stages" },
  { value: "draft",           label: "Draft" },
  { value: "submitted",       label: "Submitted" },
  { value: "routing",         label: "Routing" },
  { value: "in_review",       label: "In Review" },
  { value: "approved",        label: "Approved" },
  { value: "analyst_working", label: "Analyst Working" },
  { value: "attorney_review", label: "Attorney Review" },
  { value: "gc_review",       label: "GC Review" },
  { value: "vendor_review",   label: "Vendor Review" },
  { value: "legal_approved",  label: "Legal Approved" },
  { value: "expired",         label: "Expired" },
  { value: "terminated",      label: "Terminated" },
];

function flattenTree(roots, expanded) {
  const out = [];
  (function walk(nodes, depth) {
    for (const n of nodes) {
      out.push({ node: n, depth });
      if (expanded.has(n.id) && n.children && n.children.length) walk(n.children, depth + 1);
    }
  })(roots || [], 0);
  return out;
}

function findNodeById(roots, id) {
  const stack = [...(roots || [])];
  while (stack.length) {
    const n = stack.pop();
    if (n.id === id) return n;
    if (n.children) stack.push(...n.children);
  }
  return null;
}

export default function Repository() {
  const nav = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const canManage = user && MANAGE_ROLES.has(user.role);

  // Default tab reads `?status=` from URL so external links can deep-link.
  const initialStatus = new URLSearchParams(location.search).get("status") === "executed" ? "executed" : "non_executed";
  const [status, setStatus] = useState(initialStatus);

  const [tree, setTree] = useState({ roots: [], total_units: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [contracts, setContracts] = useState([]);
  const [view, setView] = useState("list");
  const [includeDescendants, setIncludeDescendants] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [selection, setSelection] = useState(new Set());
  const [globalMode, setGlobalMode] = useState(false);
  const [globalResults, setGlobalResults] = useState(null);
  const [searchFocus, setSearchFocus] = useState(false);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("repo:recent-searches") || "[]"); }
    catch { return []; }
  });
  const [pinned, setPinned] = useState([]);
  const canBulk = user && BULK_ROLES.has(user.role);

  const loadPinned = () => {
    api.get("/saved-views?scope=search")
      .then(r => setPinned((r.data || []).map(v => ({ id: v.id, name: v.name, q: v.filters?.q || v.name }))))
      .catch(() => setPinned([]));
  };
  useEffect(() => { loadPinned(); }, []);

  const pushRecent = (term) => {
    const t = (term || "").trim();
    if (t.length < 2) return;
    setRecent(prev => {
      const next = [t, ...prev.filter(x => x !== t)].slice(0, 5);
      localStorage.setItem("repo:recent-searches", JSON.stringify(next));
      return next;
    });
  };
  const pinSearch = async (term) => {
    const t = (term || "").trim();
    if (!t) return;
    if (pinned.find(p => p.q === t)) return toast.info("Already pinned");
    try {
      await api.post("/saved-views", { name: t, filters: { scope: "search", q: t } });
      toast.success("Pinned");
      loadPinned();
    } catch (e) { toast.error(e?.response?.data?.detail || "Pin failed"); }
  };
  const unpinSearch = async (id) => {
    try { await api.delete(`/saved-views/${id}`); loadPinned(); }
    catch { toast.error("Unpin failed"); }
  };

  const loadTree = async () => {
    try {
      const { data } = await api.get("/org/tree");
      setTree(data);
      if (!selectedId && data.roots && data.roots.length) setSelectedId(data.roots[0].id);
      if (expanded.size === 0 && data.roots) setExpanded(new Set(data.roots.map(r => r.id)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load tree"); }
  };

  const loadContracts = async () => {
    if (!selectedId) return setContracts([]);
    setLoadingContracts(true);
    try {
      const { data } = await api.get(`/org/units/${selectedId}/contracts?include_descendants=${includeDescendants}`);
      setContracts(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load contracts"); }
    finally { setLoadingContracts(false); }
  };

  useEffect(() => { loadTree(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadContracts(); /* eslint-disable-next-line */ }, [selectedId, includeDescendants]);
  useEffect(() => { setStageFilter("all"); setSelection(new Set()); }, [status]);
  useEffect(() => { setSelection(new Set()); }, [selectedId, includeDescendants]);

  const toggleSel = (id) => {
    setSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Cross-folder search — hits /org/contracts/search when the "All folders"
  // toggle is on and the search term is ≥2 chars. Results carry a folder_path
  // breadcrumb so the caller can render "found in Division X".
  useEffect(() => {
    if (!globalMode || search.trim().length < 2) { setGlobalResults(null); return; }
    const controller = new AbortController();
    const t = setTimeout(() => {
      api.get(`/org/contracts/search?q=${encodeURIComponent(search.trim())}&status=${status}`, { signal: controller.signal })
        .then(r => { setGlobalResults(r.data || []); pushRecent(search.trim()); })
        .catch(() => { if (!controller.signal.aborted) setGlobalResults([]); });
    }, 300);
    return () => { clearTimeout(t); controller.abort(); };
  }, [globalMode, search, status]);

  const toggleExpand = (id) => {
    const s = new Set(expanded);
    if (s.has(id)) s.delete(id); else s.add(id);
    setExpanded(s);
  };

  const selectedNode = findNodeById(tree.roots, selectedId);
  const flat = flattenTree(tree.roots, expanded);

  // First apply status split, then stage / search filters.
  const filtered = useMemo(() => {
    let list = contracts.filter(c => {
      const s = (c.stage || c.status || "").toLowerCase();
      if (status === "archive") return s === "executed" && isArchive(c);
      if (status === "executed") return s === "executed" && !isArchive(c);
      return NON_EXEC_STAGES.has(s);
    });
    if (status === "non_executed" && stageFilter !== "all") {
      list = list.filter(c => (c.stage || c.status || "").toLowerCase() === stageFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(c =>
        (c.vendor_legal_name || "").toLowerCase().includes(q) ||
        (c.contract_number || "").toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q) ||
        (c.purpose || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [contracts, status, stageFilter, search]);

  const nonExecCount = useMemo(() => contracts.filter(c => NON_EXEC_STAGES.has((c.stage || c.status || "").toLowerCase())).length, [contracts]);
  const execCount    = useMemo(() => contracts.filter(c => (c.stage || c.status || "").toLowerCase() === "executed" && !isArchive(c)).length, [contracts]);
  const archiveCount = useMemo(() => contracts.filter(c => (c.stage || c.status || "").toLowerCase() === "executed" && isArchive(c)).length, [contracts]);

  return (
    <div className="p-6" data-testid="repository-page">
      <div className="mb-4 flex items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5">
            <FolderTree className="w-3.5 h-3.5" /> Organization Repository
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Contract Repository</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every contract across the Healthcare District, filed by Division &amp; Business Unit. Switch tabs to move between contracts still in-flight and the fully executed archive.
          </p>
        </div>
        <button onClick={loadTree} data-testid="repo-refresh" className="ml-auto h-9 px-3 text-xs font-semibold border border-slate-200 rounded flex items-center gap-1 hover:bg-slate-50">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
        {canManage && (
          <button onClick={async () => {
            if (!window.confirm("Auto-place contracts uses each contract's intake `department` to file it under the matching org-unit folder. Contracts already assigned are kept. Proceed?")) return;
            try {
              const { data } = await api.post("/org/auto-place");
              toast.success(`Auto-placed ${data.assigned} contracts (${data.unmatched} unmatched)`);
              loadTree();
              if (selectedId) loadContracts();
            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
          }} data-testid="repo-auto-place" className="ml-2 h-9 px-3 text-xs font-semibold bg-[#005289] text-white rounded flex items-center gap-1 hover:opacity-90">
            <RefreshCw className="w-3 h-3" /> Auto-place Contracts
          </button>
        )}
      </div>

      {/* Status tabs — non-executed vs executed */}
      <div className="border-b border-slate-200 flex gap-1 mb-4">
        <button onClick={() => setStatus("non_executed")}
                className={`px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 ${status === "non_executed" ? "border-[#005289] text-[#005289]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                data-testid="repo-tab-non-executed">
          <Clock className="w-3.5 h-3.5" /> Non-Executed
          <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${status === "non_executed" ? "bg-[#005289] text-white" : "bg-slate-100 text-slate-500"}`}>{nonExecCount}</span>
        </button>
        <button onClick={() => setStatus("executed")}
                className={`px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 ${status === "executed" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                data-testid="repo-tab-executed">
          <CheckCircle2 className="w-3.5 h-3.5" /> Executed
          <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${status === "executed" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>{execCount}</span>
        </button>
        <button onClick={() => setStatus("archive")}
                className={`px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 ${status === "archive" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                data-testid="repo-tab-archive"
                title="Contracts whose end date is more than 7 years past">
          <Archive className="w-3.5 h-3.5" /> Archive (&gt;7yr)
          <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${status === "archive" ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-500"}`}>{archiveCount}</span>
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-4 bg-white border border-slate-200 rounded-md" data-testid="repo-tree-pane">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <FolderTree className="w-4 h-4 text-[#005289]" />
            <div className="text-sm font-bold text-slate-900">Organization ({tree.total_units})</div>
            {canManage && (
              <button onClick={() => setDialog({ mode: "create", parent_id: null })} data-testid="repo-add-root" className="ml-auto h-7 px-2 text-[11px] font-semibold border border-slate-200 rounded flex items-center gap-1 hover:bg-slate-50">
                <Plus className="w-3 h-3" /> Add Division
              </button>
            )}
          </div>
          <div className="p-2 max-h-[70vh] overflow-y-auto" data-testid="repo-tree-list">
            {flat.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No units yet.</div>}
            {flat.map(({ node, depth }) => {
              const isSel = selectedId === node.id;
              const hasKids = (node.children && node.children.length) > 0;
              const isOpen = expanded.has(node.id);
              return (
                <div key={node.id} data-testid={`repo-node-${node.id}`}
                     className={`flex items-center gap-1 px-2 py-1.5 rounded text-sm ${isSel ? "bg-[#005289]/10 text-[#005289] font-semibold" : "hover:bg-slate-50 text-slate-700"}`}
                     style={{ paddingLeft: 8 + depth * 14 }}>
                  <button className="w-4 h-4 flex items-center justify-center flex-shrink-0" onClick={() => hasKids && toggleExpand(node.id)}>
                    {hasKids ? (isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />) : <span className="w-3" />}
                  </button>
                  <button onClick={() => setSelectedId(node.id)} className="flex items-center gap-1 flex-1 min-w-0 text-left cursor-pointer">
                    {isOpen && hasKids ? <FolderOpen className="w-4 h-4 text-amber-600" /> : <Folder className="w-4 h-4 text-slate-500" />}
                    <span className="truncate flex-1">{node.name}</span>
                    <span className="text-[10px] text-slate-400">({node.total_contract_count || 0})</span>
                  </button>
                  {canManage && (
                    <>
                      <button onClick={() => setDialog({ mode: "create", parent_id: node.id })} title="Add child" data-testid={`repo-add-child-${node.id}`} className="text-slate-400 hover:text-[#005289]">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => setDialog({ mode: "edit", unit: node })} title="Edit" data-testid={`repo-edit-${node.id}`} className="text-slate-400 hover:text-[#005289]">
                        <Edit2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="col-span-8 space-y-3" data-testid="repo-contracts-pane">
          <div className="bg-white border border-slate-200 rounded-md px-4 py-3 flex flex-wrap items-center gap-3">
            <Building2 className="w-4 h-4 text-[#005289]" />
            <div>
              <div className="text-sm font-bold text-slate-900" data-testid="repo-selected-name">{selectedNode ? selectedNode.name : "— select a unit —"}</div>
              {selectedNode && (
                <div className="text-[11px] text-slate-500">
                  {selectedNode.type === "division" ? "Division" : "Business Unit"} · {selectedNode.direct_contract_count} direct · {selectedNode.total_contract_count} total (incl. sub-units)
                </div>
              )}
            </div>
            <label className="ml-auto text-[11px] text-slate-600 flex items-center gap-1">
              <input type="checkbox" data-testid="repo-include-desc" checked={includeDescendants} onChange={e => setIncludeDescendants(e.target.checked)} />
              Include sub-units
            </label>
            {status === "non_executed" && (
              <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}
                      data-testid="repo-stage-filter"
                      className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
                {NON_EXEC_STAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            <div className="relative">
              <SearchIcon className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                     onFocus={() => setSearchFocus(true)}
                     onBlur={() => setTimeout(() => setSearchFocus(false), 200)}
                     placeholder={globalMode ? "Search ALL folders…" : "Vendor, number, description…"} data-testid="repo-search"
                     className="h-7 pl-6 pr-2 text-xs w-52 border border-slate-200 rounded-md bg-white text-slate-700 focus:border-[#005289]" />
              {globalMode && searchFocus && (recent.length + pinned.length > 0) && (
                <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-slate-200 rounded-md shadow-lg z-20 max-h-72 overflow-y-auto"
                     data-testid="repo-search-history">
                  {pinned.length > 0 && (
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1.5 bg-slate-50 flex items-center gap-1"><Pin className="w-3 h-3" /> Pinned</div>
                      {pinned.map(p => (
                        <div key={p.id} className="flex items-center px-3 py-1.5 hover:bg-slate-50 text-xs" data-testid={`repo-pinned-${p.id}`}>
                          <button onClick={() => setSearch(p.q)} className="flex-1 text-left truncate text-slate-700">{p.q}</button>
                          <button onClick={() => unpinSearch(p.id)} title="Unpin" className="text-slate-400 hover:text-red-500 ml-2"
                                  data-testid={`repo-unpin-${p.id}`}>
                            <PinOff className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {recent.length > 0 && (
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1.5 bg-slate-50 flex items-center gap-1"><History className="w-3 h-3" /> Recent</div>
                      {recent.map((r, i) => (
                        <div key={i} className="flex items-center px-3 py-1.5 hover:bg-slate-50 text-xs" data-testid={`repo-recent-${i}`}>
                          <button onClick={() => setSearch(r)} className="flex-1 text-left truncate text-slate-700">{r}</button>
                          <button onClick={() => pinSearch(r)} title="Pin" className="text-slate-400 hover:text-[#005289] ml-2"
                                  data-testid={`repo-pin-${i}`}>
                            <Pin className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <label className="inline-flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
              <input type="checkbox" checked={globalMode} onChange={e => setGlobalMode(e.target.checked)}
                     data-testid="repo-global-search-toggle" />
              All folders
            </label>
            {globalMode && search.trim().length >= 2 && !pinned.find(p => p.q === search.trim()) && (
              <button onClick={() => pinSearch(search)} title="Pin this search"
                      data-testid="repo-pin-current"
                      className="h-7 w-7 flex items-center justify-center border border-slate-200 rounded hover:bg-slate-50 text-slate-500 hover:text-[#005289]">
                <Pin className="w-3 h-3" />
              </button>
            )}
            <div className="flex border border-slate-200 rounded-md overflow-hidden">
              <button onClick={() => setView("list")} data-testid="repo-view-list" className={`h-7 px-2 text-xs flex items-center gap-1 ${view === "list" ? "bg-[#005289] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                <List className="w-3 h-3" /> List
              </button>
              <button onClick={() => setView("tree")} data-testid="repo-view-tree" className={`h-7 px-2 text-xs flex items-center gap-1 ${view === "tree" ? "bg-[#005289] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                <LayoutGrid className="w-3 h-3" /> Cards
              </button>
            </div>
          </div>

          {globalMode ? (
            search.trim().length < 2 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-md p-8 text-center text-sm text-slate-400">Type at least 2 characters to search every folder…</div>
            ) : globalResults === null ? (
              <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-400">Searching…</div>
            ) : globalResults.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-400" data-testid="repo-global-empty">
                No contracts match “{search}” across all folders.
              </div>
            ) : (
              <div className="space-y-2" data-testid="repo-global-results">
                <div className="text-[11px] text-slate-500">Found <b>{globalResults.length}</b> match{globalResults.length === 1 ? "" : "es"} across all folders</div>
                <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                  {globalResults.map(r => (
                    <button key={r.id} onClick={() => nav(`/contract/${r.id}`)}
                            data-testid={`repo-global-row-${r.contract_number}`}
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-mono text-slate-500">#{r.contract_number}</span>
                          <span className="font-semibold text-slate-900 truncate">{r.vendor_legal_name}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase ${(r.stage || "") === "executed" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{(r.stage || "").replace(/_/g, " ")}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5 truncate">{r.description || r.purpose || "—"}</div>
                        <div className="text-[10px] text-[#005289] font-semibold mt-0.5 flex items-center gap-1">
                          <FolderTree className="w-3 h-3" /> {r.folder_path}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )
          ) : loadingContracts ? (
            <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-400" data-testid="repo-empty">
              {status === "executed"
                ? `No executed contracts under ${selectedNode ? selectedNode.name : "this unit"} yet.`
                : contracts.length === 0
                  ? `No contracts under ${selectedNode ? selectedNode.name : "this unit"} yet.`
                  : `No contracts match the current stage / search filter.`}
            </div>
          ) : view === "list" ? (
            <>
              {canBulk && status === "non_executed" && (
                <RepoBulkBar selection={selection} contracts={filtered}
                             onClear={() => setSelection(new Set())}
                             onDone={() => { setSelection(new Set()); loadContracts(); }} />
              )}
              <TrackerTable rows={filtered} onChanged={loadContracts} testIdPrefix="repo-tt" emptyText="—"
                            selection={canBulk && status === "non_executed" ? selection : null}
                            onToggle={canBulk && status === "non_executed" ? toggleSel : null} />
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="repo-tree-cards">
              {filtered.map(c => <ContractCard key={c.id} c={c} />)}
            </div>
          )}
        </div>
      </div>

      {dialog && <UnitDialog dialog={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); loadTree(); }} />}
    </div>
  );
}

function ContractCard({ c }) {
  const nav = useNavigate();
  return (
    <button onClick={() => nav(`/contract/${c.id}`)} data-testid={`repo-card-${c.contract_number}`}
            className="text-left bg-white border border-slate-200 rounded-md p-3 hover:border-[#005289] hover:shadow-sm">
      <div className="flex items-start justify-between mb-1">
        <div className="text-xs font-mono text-slate-400">{c.contract_number}</div>
      </div>
      <div className="font-semibold text-sm text-slate-900 truncate">{c.vendor_legal_name}</div>
      <div className="text-xs text-slate-500 truncate">{c.description || c.purpose || "—"}</div>
      <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-500">
        <span>{c.contract_type}</span>·<span>{c.department}</span>·<span>{c.timeline}</span>
      </div>
    </button>
  );
}

function UnitDialog({ dialog, onClose, onSaved }) {
  const isEdit = dialog.mode === "edit";
  const [name, setName] = useState(isEdit ? (dialog.unit && dialog.unit.name) || "" : "");
  const [type, setType] = useState(isEdit ? (dialog.unit && dialog.unit.type) || "business_unit" : (dialog.parent_id ? "business_unit" : "division"));
  const [description, setDescription] = useState(isEdit ? (dialog.unit && dialog.unit.description) || "" : "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/org/units/${dialog.unit.id}`, { name: name.trim(), type, description });
        toast.success("Updated");
      } else {
        await api.post("/org/units", { name: name.trim(), parent_id: dialog.parent_id, type, description });
        toast.success("Created");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const archive = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Archive ${dialog.unit.name}?`)) return;
    setBusy(true);
    try { await api.delete(`/org/units/${dialog.unit.id}`); toast.success("Archived"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose} data-testid="repo-unit-dialog">
      <div className="bg-white rounded-md w-[440px] max-w-[90vw]" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Folder className="w-4 h-4 text-[#005289]" />
          <div className="font-bold text-slate-900">{isEdit ? "Edit unit" : "New unit"}</div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Name *</span>
            <input value={name} onChange={e => setName(e.target.value)} data-testid="repo-dialog-name" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md" placeholder="e.g., Healey Center" />
          </label>
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Type</span>
            <select value={type} onChange={e => setType(e.target.value)} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md">
              <option value="division">Division</option>
              <option value="business_unit">Business Unit</option>
            </select>
          </label>
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Description</span>
            <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} className="w-full p-2 text-sm border border-slate-200 rounded-md" placeholder="Optional" />
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
          {isEdit && (
            <button onClick={archive} disabled={busy} className="h-9 px-3 text-xs font-semibold text-red-600 border border-red-200 rounded flex items-center gap-1 hover:bg-red-50">
              <Archive className="w-3 h-3" /> Archive
            </button>
          )}
          <button onClick={onClose} className="ml-auto h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="repo-dialog-save" className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white disabled:opacity-60">
            {busy ? "Saving…" : (isEdit ? "Update" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}
